/*
 * aura-car-3d.js: <aura-car-3d>, the product's 3D tab (spec 08, snippets/car-3d.liquid).
 * Frames the Color Explorer's embed (Explorer4 ?embed&color=<handle>, its docs/plan/15) the first time the panel is
 * on screen, and lets the frame show its own loader. The embed draws its own car button and Studio | Outdoor switch
 * with its time of day, so a pick happens inside the frame and the frame is never reloaded for it.
 * The poster stays hidden unless the car can't come: no WebGL, Save-Data or low memory (until the shopper asks for
 * it), a frame `error` or no `ready` in time. Then it shows with the reason.
 * Skips the frame on Save-Data and low-memory devices until the shopper asks for it, and without WebGL 2.
 * Maps the frame's messages onto spec 08's events (aura_car_interacted, aura_car_live_loaded,
 * aura_car_light_changed, aura_car_changed, aura_car_live_failed).
 */
(() => {
  const track = (n, p) => window.aura && window.aura.track(n, p);
  const SOURCE = 'aura-explorer';
  const LOAD_TIMEOUT_MS = 60000; // no `ready` by then: say so on the poster (a late `ready` still lifts it)

  /* Shopify's t filter HTML-escapes ("Couldn&#39;t"); these strings go in as text */
  function decodeAll(obj) {
    const t = document.createElement('textarea');
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => { t.innerHTML = v; return [k, t.value]; }));
  }

  function deviceTier() {
    const mem = navigator.deviceMemory;
    if (!mem) return null;
    return mem >= 8 ? 'high' : mem >= 4 ? 'mid' : 'low';
  }

  /* '' when the device can take the live car; otherwise why not */
  function blocker() {
    try {
      const gl = document.createElement('canvas').getContext('webgl2');
      if (!gl) return 'no_webgl';
      const lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    } catch (e) { return 'no_webgl'; }
    if (navigator.connection && navigator.connection.saveData) return 'heavy';
    if (navigator.deviceMemory && navigator.deviceMemory < 4) return 'heavy';
    return '';
  }

  class AuraCar3d extends HTMLElement {
    connectedCallback() {
      this.base = (this.dataset.explorer || '').replace(/\/$/, '');
      try { this.origin = new URL(this.base).origin; } catch (e) { this.origin = null; }
      this.strings = decodeAll(JSON.parse(this.querySelector('[data-car3d-strings]').textContent));
      this.frameBox = this.querySelector('[data-car3d-frame]');
      this.poster = this.querySelector('[data-car3d-poster]');
      this.statusBox = this.querySelector('[data-car3d-status-box]');
      this.status = this.querySelector('[data-car3d-status]');
      this.loadBtn = this.querySelector('[data-car3d-load]');
      this.view = null;    // what the frame says is on show: {car, scene, stop}
      this.loaded = false; // the first `ready` came (tracked once)

      this.onMessage = this.onMessage.bind(this);
      window.addEventListener('message', this.onMessage);
      this.loadBtn.addEventListener('click', () => { this.loadBtn.hidden = true; this.start(); });

      if (!this.origin) { this.poster.classList.remove('is-gone'); return; }
      /* The panel is display:none until its tab is picked, so it intersects only once it shows */
      if (!('IntersectionObserver' in window)) { this.begin(); return; }
      this.io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.io.disconnect();
        this.begin();
      });
      this.io.observe(this);
    }

    disconnectedCallback() {
      window.removeEventListener('message', this.onMessage);
      if (this.io) this.io.disconnect();
      clearTimeout(this.loadTimer);
    }

    begin() {
      const why = blocker();
      if (!why) { this.start(); return; }
      this.poster.classList.remove('is-gone');
      this.poster.classList.add('is-idle');
      if (why === 'heavy') this.loadBtn.hidden = false;
      this.say(this.strings[why]);
    }

    url() {
      const q = new URLSearchParams({ color: this.dataset.handle });
      return `${this.base}/?embed&${q}`;
    }

    start() {
      this.poster.classList.remove('is-idle', 'is-failed');
      this.poster.classList.add('is-gone');
      this.say('');
      if (!this.iframe) {
        this.iframe = document.createElement('iframe');
        this.iframe.title = this.strings.title;
        this.iframe.allow = 'fullscreen';
        this.iframe.src = this.url();
        this.frameBox.append(this.iframe);
      }
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => { if (!this.loaded) this.fail(this.strings.offline); }, LOAD_TIMEOUT_MS);
    }

    /* The poster's line: why the car can't come (or that it needs a tap) */
    say(text) {
      this.status.textContent = text || '';
      this.statusBox.hidden = !text && this.loadBtn.hidden;
    }

    fail(text) {
      this.poster.classList.remove('is-gone');
      this.poster.classList.add('is-failed');
      this.say(text);
    }

    onMessage(e) {
      const m = e.data;
      if (!this.iframe || e.source !== this.iframe.contentWindow || e.origin !== this.origin) return;
      if (!m || typeof m !== 'object' || m.source !== SOURCE) return;
      if (m.type === 'ready') this.onReady(m);
      else if (m.type === 'change') this.onChange(m);
      else if (m.type === 'interact') track('aura_car_interacted', { handle: this.dataset.handle, from: 'poster', to: 'live', surface: 'pdp' });
      else if (m.type === 'error') this.onError(m.reason);
    }

    onReady(m) {
      clearTimeout(this.loadTimer);
      this.view = { car: m.car, scene: m.scene, stop: m.stop };
      this.poster.classList.add('is-gone');
      this.say('');
      if (!this.loaded) track('aura_car_live_loaded', { car: m.car, ms: m.ms, device_tier: deviceTier() });
      this.loaded = true;
    }

    onChange(m) {
      const before = this.view || {};
      this.view = { car: m.car, scene: m.scene, stop: m.stop };
      if (m.scene !== before.scene || (m.scene === 'outdoor' && m.stop !== before.stop)) {
        track('aura_car_light_changed', { to: m.scene === 'outdoor' ? m.stop : m.scene });
      }
      if (m.car !== before.car) track('aura_car_changed', { car: m.car, from_garage: false });
    }

    onError(reason) {
      track('aura_car_live_failed', { reason });
      if (this.loaded) return; // the car on show stays; the explorer only reports it
      clearTimeout(this.loadTimer);
      this.fail(reason === 'offline' ? this.strings.offline : this.strings.not_in_3d);
    }
  }

  if (!customElements.get('aura-car-3d')) customElements.define('aura-car-3d', AuraCar3d);
})();
